import React from 'react';
import {
  Box, Paper, Typography, Button, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExportMenu from '../ExportMenu';
import { formatCurrency } from '../../utils/formatCurrency';
import { methodologyHref } from '../../utils/metricDefenses';
import { fmtWinRate, fmtOpenShare, winRateContext } from '../../utils/winRate';

const fmtPct = fmtWinRate;
const COVARIATE_LABELS = {
  deal_size_at_creation: 'deal size at creation',
  product_family: 'product family',
  industry: 'industry',
  geography: 'geography',
  created_year: 'created year',
};

const signed = (v) => `${v >= 0 ? '+' : ''}${v}`;

/**
 * Plain-language reading of the adjusted lift, driven by the sign and size of the numbers.
 * Returns { kind: 'positive' | 'negative' | 'near_zero', severity, headline, detail }.
 */
export function liftNarrative(regression, { rawGapPts = null, attendeeOpenShare = null, baselineOpenShare = null } = {}) {
  const ame = regression.ame_pts;
  const lo = regression.ci_lo_pts;
  const hi = regression.ci_hi_pts;
  const spansZero = lo != null && hi != null && lo <= 0 && hi >= 0;
  let kind = 'positive';
  if (Math.abs(ame) < 0.5 || spansZero) kind = 'near_zero';
  else if (ame < 0) kind = 'negative';

  const covs = (regression.covariates_used || []).map((c) => COVARIATE_LABELS[c] || c);
  const holding = covs.length ? `holding ${covs.join(', ')} constant` : 'after controlling for deal characteristics';
  const ci = lo != null && hi != null ? ` (95% CI ${signed(lo)} to ${signed(hi)} pp)` : '';
  const n = regression.n_attendee != null && regression.n_baseline != null
    ? `${regression.n_attendee.toLocaleString()} attendee and ${regression.n_baseline.toLocaleString()} baseline closed deals`
    : 'the closed deals in the model';
  const win = regression.training_window?.created_from
    ? ` created on or after ${regression.training_window.created_from}${regression.training_window.basis ? ` (${regression.training_window.basis})` : ''}`
    : '';

  let headline;
  if (kind === 'positive') {
    headline = `${holding}, TEC attendees' deals closed won ${Math.abs(ame)} pp more often than comparable baseline deals${ci}.`;
  } else if (kind === 'negative') {
    headline = `${holding}, TEC attendees' deals closed won ${Math.abs(ame)} pp less often than comparable baseline deals${ci}. That gap can reflect which deals SEs choose to join, so it is not a measure of TEC's effect.`;
  } else {
    headline = `${holding}, the win rate gap is ${signed(ame)} pp${ci}, which is not distinguishable from zero. The model finds no clear win rate difference between attendees and the baseline.`;
  }

  const parts = [`Fitted on ${n}${win}.`];
  if (rawGapPts != null) {
    parts.push(`The unadjusted gap is ${signed(rawGapPts.toFixed(1))} pp; the difference between that and the adjusted figure reflects deal mix, mainly size and product.`);
  }
  const open = [
    attendeeOpenShare != null ? `attendees ${fmtOpenShare(attendeeOpenShare)}` : null,
    baselineOpenShare != null ? `baseline ${fmtOpenShare(baselineOpenShare)}` : null,
  ].filter(Boolean);
  if (open.length) {
    parts.push(`Win rates ignore deals still open (${open.join(', ')}), so a group with more open deals can look better or worse than it finally will.`);
  }
  parts.push('Association, not causation.');
  return { kind, severity: kind === 'positive' ? 'success' : kind === 'negative' ? 'warning' : 'info', headline, detail: parts.join(' ') };
}
const fmtCurrency = (v) => formatCurrency(v, { emptyDisplay: '—' });

function DealVolumeTable({ bands }) {
  if (!bands || bands.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No deal volume data available for this period.
      </Alert>
    );
  }
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Where TEC attendees' deals sit — closed deal counts by size tier
        </Typography>
        <ExportMenu
          title="Deal volume by size tier"
          data={bands.map((b) => {
            const total = (b.attendee_n || 0) + (b.baseline_n || 0);
            return {
              band: b.band,
              attendee_n: b.attendee_n || 0,
              baseline_n: b.baseline_n || 0,
              share: total > 0 ? `${((b.attendee_n || 0) / total * 100).toFixed(1)}%` : '',
              attendee_open: b.attendee_open_share != null ? fmtOpenShare(b.attendee_open_share) : '',
              baseline_open: b.baseline_open_share != null ? fmtOpenShare(b.baseline_open_share) : '',
            };
          })}
          columns={[
            { header: 'Deal Size', key: 'band' },
            { header: 'TEC attendees (closed n)', key: 'attendee_n' },
            { header: 'Baseline (closed n)', key: 'baseline_n' },
            { header: 'Attendee share', key: 'share' },
            { header: 'Attendee open share', key: 'attendee_open' },
            { header: 'Baseline open share', key: 'baseline_open' },
          ]}
        />
      </Box>
      <Table size="small" aria-label="Deal volume by size tier">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Deal Size</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>TEC attendees (closed n)</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Baseline (closed n)</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Attendee share</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Attendee open</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textAlign: 'right' }}>Baseline open</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((b) => {
            const total = (b.attendee_n || 0) + (b.baseline_n || 0);
            const share = total > 0 ? ((b.attendee_n || 0) / total * 100).toFixed(1) + '%' : '—';
            return (
              <TableRow key={b.band} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{b.band}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                  {(b.attendee_n || 0).toLocaleString()}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>
                  {(b.baseline_n || 0).toLocaleString()}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: '#1976d2', fontWeight: 600 }}>
                  {share}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: 'text.secondary' }}>
                  {b.attendee_open_share != null ? `${Math.round(b.attendee_open_share * 100)}%` : '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right', color: 'text.secondary' }}>
                  {b.baseline_open_share != null ? `${Math.round(b.baseline_open_share * 100)}%` : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Attendee share = attendee closed deals ÷ all closed deals in that tier. Open columns show the share of deals
        in the tier still undecided; the win rates above leave them out. A very different deal-size mix between the two groups is why
        the raw gap and the adjusted figure can differ.
      </Typography>
    </Box>
  );
}

function AdjustedLiftPanel({ regression, rawGapPts, attendeeOpenShare, baselineOpenShare }) {
  if (regression == null) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Adjusted lift model is computing — check back in a few minutes.
      </Alert>
    );
  }
  if (regression.fit_error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Model fit failed: {regression.error || 'unknown error'}. Will retry on the next 24-hour cycle.
      </Alert>
    );
  }
  if (regression.insufficient_data) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Not enough closed opportunities to fit the model (requires ≥200 total, ≥30 per group).
        Results will appear as deal volume grows.
      </Alert>
    );
  }

  const ame = regression.ame_pts;
  const ciLo = regression.ci_lo_pts;
  const ciHi = regression.ci_hi_pts;
  const pval = regression.p_value;
  const nAtt = regression.n_attendee;
  const nBl = regression.n_baseline;
  const fittedAt = regression.fitted_at
    ? new Date(regression.fitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  const narrative = liftNarrative(regression, { rawGapPts, attendeeOpenShare, baselineOpenShare });
  const ameColor = narrative.kind === 'positive' ? '#2e7d32' : narrative.kind === 'negative' ? '#e65100' : '#616161';
  const pSig = pval != null && pval < 0.05;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: ameColor }}>
          {ame >= 0 ? '+' : ''}{ame} pp
        </Typography>
        <Tooltip
          title={
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Average marginal effect of TEC attendance on the chance a closed deal is won, after controlling for
                deal size at creation, product family, industry, and geography.
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                95% CI: [{ciLo != null ? `${ciLo >= 0 ? '+' : ''}${ciLo}` : '—'} pp,
                {ciHi != null ? ` ${ciHi >= 0 ? '+' : ''}${ciHi}` : ' —'} pp]
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                p={pval != null ? pval.toFixed(4) : '—'} · n={nAtt?.toLocaleString()} attendee, {nBl?.toLocaleString()} baseline
              </Typography>
            </Box>
          }
          arrow
        >
          <InfoOutlinedIcon sx={{ mt: 1, fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">p-value</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: pSig ? '#2e7d32' : 'text.secondary' }}>
            {pval != null ? pval.toFixed(4) : '—'}{pSig ? ' ✓' : ''}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">TEC attendees (n)</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{nAtt?.toLocaleString() ?? '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Baseline (n)</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{nBl?.toLocaleString() ?? '—'}</Typography>
        </Box>
      </Box>

      {regression.covariates_used && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Controls: {regression.covariates_used.map((c) => COVARIATE_LABELS[c] || c).join(', ')}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mb: 2 }}>
        Association, not causation — selection bias may persist. Fit: {fittedAt ?? '—'}.
        {regression.training_window?.created_from
          ? ` Model uses closed deals created on or after ${regression.training_window.created_from}${regression.training_window.basis ? ` (${regression.training_window.basis})` : ''}, not the selected date range.`
          : ' Model does not use the selected date range.'}
      </Typography>

      <Alert severity={narrative.severity} sx={{ fontSize: '0.75rem' }} aria-label="Adjusted lift reading">
        <strong>
          {narrative.kind === 'positive' ? 'Adjusted lift is positive.' : narrative.kind === 'negative' ? 'Adjusted lift is negative.' : 'Adjusted lift is near zero.'}
        </strong>{' '}
        {narrative.headline} {narrative.detail}{' '}
        <a href={methodologyHref('win_rate_regression')}>Why trust this number?</a>
      </Alert>
    </Box>
  );
}

export default function WinRateDrillPanel({
  bands, regression, open, onClose,
  attendeeWinRate, baselineWinRate, attendeeN, baselineN,
  attendeeAvgDealSize, baselineAvgDealSize, attendeeOpenShare, baselineOpenShare,
}) {
  if (!open) return null;

  const lift = attendeeWinRate != null && baselineWinRate != null
    ? (attendeeWinRate - baselineWinRate) * 100
    : null;
  const dealSizeRatio = attendeeAvgDealSize && baselineAvgDealSize && baselineAvgDealSize > 0
    ? (attendeeAvgDealSize / baselineAvgDealSize).toFixed(1)
    : null;

  return (
    <Paper
      variant="outlined"
      aria-label="Win rate drill panel"
      sx={{ mt: 1, p: 2, borderLeft: '4px solid #1976d2' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Win Rate — Adjusted for Deal Complexity
        </Typography>
        <Button size="small" onClick={onClose} sx={{ minWidth: 0, p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Raw rates + deal size context — detail that lives here, not on the card */}
        {(attendeeWinRate !== undefined || baselineWinRate !== undefined) && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Raw Win Rates
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{fmtPct(attendeeWinRate)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  TEC attendees{winRateContext({ n: attendeeN, openShare: attendeeOpenShare }) ? ` (${winRateContext({ n: attendeeN, openShare: attendeeOpenShare }, ', ')})` : ''}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ alignSelf: 'center' }}>vs</Typography>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{fmtPct(baselineWinRate)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Non-Attendees{winRateContext({ n: baselineN, openShare: baselineOpenShare }) ? ` (${winRateContext({ n: baselineN, openShare: baselineOpenShare }, ', ')})` : ''}
                </Typography>
              </Box>
              {lift != null && (
                <Box sx={{ alignSelf: 'flex-end' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#757575' }}>
                    Raw: {lift >= 0 ? '+' : ''}{lift.toFixed(1)} pp
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    (unadjusted — compares different deal mixes and ignores open deals)
                  </Typography>
                </Box>
              )}
            </Box>

            {attendeeAvgDealSize != null && baselineAvgDealSize != null && (
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>{fmtCurrency(attendeeAvgDealSize)}</Typography>
                  <Typography variant="caption" color="text.secondary">TEC attendee avg deal size</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>vs</Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtCurrency(baselineAvgDealSize)}</Typography>
                  <Typography variant="caption" color="text.secondary">Baseline avg deal size</Typography>
                </Box>
                {dealSizeRatio && parseFloat(dealSizeRatio) > 1 && (
                  <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600, alignSelf: 'center' }}>
                    Attendee deals are {dealSizeRatio}× larger on average
                  </Typography>
                )}
              </Box>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        <DealVolumeTable bands={bands} />
        <AdjustedLiftPanel regression={regression} rawGapPts={lift} attendeeOpenShare={attendeeOpenShare} baselineOpenShare={baselineOpenShare} />
      </Box>
    </Paper>
  );
}
